/* eslint-disable react-hooks/exhaustive-deps */
import { RateConversion } from '@constants/rate-conversion';
import { useAuthState, useReviewState } from '@core/.';
import { GoogleReview } from '@core/models';
import { Button, Card, Carousel, Col, Divider, Empty, Row, Skeleton, Switch, Tooltip } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import moment from 'moment';
import { useEffect, useState } from 'react';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { heroApiService } from '@services/service-register';
import { Widget, WidgetConfig } from '@core/models/widget';
import type { RadioChangeEvent } from 'antd';
import { Radio } from 'antd';
import { useBreakpoint } from '@core/hooks/useBreakpoint';
import ReviewWidget from '@components/ReviewWidget';
import {
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DesktopOutlined,
  MobileOutlined,
} from '@ant-design/icons';
import LoadableContainer from '@components/LoadableContainer';

type Props = {};

const WidgetScreen = (_: Props) => {
  const { user } = useAuthState();
  const { reviewResponse, isLoading } = useReviewState();
  const [isLoadingWidget, setIsLoadingWidget] = useState(false);
  const [widget, setWidget] = useState<Widget>();
  const { isMobile } = useBreakpoint();
  const [device, setDevice] = useState('desktop');
  const [featuredReviews, setFeaturedReviews] = useState<GoogleReview[]>([]);
  const [isCopied, setIsCopied] = useState(false);
  const isLoaded = user && user.selected_location;
  const MAX_REVIEWS = 100;
  const BACKGROUND_COLOR = '#fafafa';

  const [widgetConfig, setWidgetConfig] = useState<WidgetConfig>({
    minStarRating: 4,
    hideNoCommentReviews: false,
  });

  const onChangeDevice = async (e: RadioChangeEvent) => {
    setDevice(e.target.value);
  };

  const onChangeFilterRating = (e: RadioChangeEvent) => {
    setWidgetConfig(
      prevState =>
        ({
          ...prevState,
          minStarRating: e.target.value,
        } as WidgetConfig)
    );
  };

  const saveWidget = async (locationId: string, reviews: GoogleReview[], config: WidgetConfig) => {
    try {
      const generatedWidget = await heroApiService.saveWidget(locationId, reviews, config);
      setWidget(generatedWidget);
    } catch (error) {
      console.log(error);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(
      `<div class="review-circle-widget-${widget?.location_id}" />\n<script src="https://app.reviewcircle.com/widget/index.js" defer></script>`
    );
    setIsCopied(true);
  };

  const filterReviews = async () => {
    // Filter to get latest & highest star review
    if (isLoaded && !isLoading) {
      let filteredReviews = reviewResponse.reviews.filter(
        review => RateConversion[review.starRating] >= widgetConfig.minStarRating
      );

      if (widgetConfig.hideNoCommentReviews) {
        filteredReviews = filteredReviews.filter(review => review.comment);
      }

      const featuredReviews = filteredReviews.slice(0, MAX_REVIEWS);

      setFeaturedReviews(
        [...featuredReviews].sort((reviewA, reviewB) =>
          moment(reviewA.updateTime).unix() > moment(reviewB.updateTime).unix() ? -1 : 1
        )
      );
      if (featuredReviews.length !== 0) {
        await saveWidget(user.selected_location.location_id, featuredReviews, widgetConfig);
      }
    }
  };

  const getCurrentWidget = async () => {
    if (isLoaded) {
      try {
        const widget: Widget = await heroApiService.getWidgetByLocationID(
          user.selected_location.location_id
        );
        if (widget) {
          setWidget(widget);
          setFeaturedReviews(widget.reviews);
          if (widget.config) {
            setWidgetConfig(widget.config);
          }
        } else {
          await filterReviews();
        }
      } catch (error) {
        console.log(error);
      }
    }
  };

  useEffect(() => {
    filterReviews();
  }, [widgetConfig]);

  useEffect(() => {
    setIsLoadingWidget(true);
    getCurrentWidget();
    setIsLoadingWidget(false);
  }, [reviewResponse]);

  return (
    <div className="screen-card">
      <h3>Showcase Reviews</h3>
      <Divider />
      <Card>
        <LoadableContainer isLoading={isLoading} loadComponent={<Skeleton />}>
          {isLoaded ? (
            <Row style={{ marginTop: '10px' }}>
              <Col sm={24} xl={11}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <h3>Preview</h3>
                  <Radio.Group onChange={onChangeDevice} value={device}>
                    <Radio.Button value="mobile">
                      <MobileOutlined />
                    </Radio.Button>
                    <Radio.Button value="desktop">
                      <DesktopOutlined />
                    </Radio.Button>
                  </Radio.Group>
                </div>

                <Row style={{ marginInline: 0, gap: '10px' }}>
                  <Col style={{ display: 'flex', alignItems: 'center' }}>
                    <h4>Star ratings:</h4>
                  </Col>
                  <Col>
                    <Radio.Group onChange={onChangeFilterRating} value={widgetConfig.minStarRating}>
                      <Radio value={4}>Include 4-star reviews</Radio>
                      <Radio value={5}>5-star reviews only</Radio>
                    </Radio.Group>
                  </Col>
                </Row>

                <Row style={{ marginInline: 0, gap: '10px' }}>
                  <Col style={{ display: 'flex', alignItems: 'center' }}>
                    <h4>Hide reviews without comment:</h4>
                  </Col>
                  <Col>
                    <Switch
                      size="small"
                      checkedChildren={<CheckOutlined />}
                      unCheckedChildren={<CloseOutlined />}
                      defaultChecked={widgetConfig.hideNoCommentReviews}
                      onChange={() =>
                        setWidgetConfig(
                          prevState =>
                            ({
                              ...prevState,
                              hideNoCommentReviews: !prevState.hideNoCommentReviews,
                            } as WidgetConfig)
                        )
                      }
                    />
                  </Col>
                </Row>

                <LoadableContainer isLoading={isLoadingWidget} loadComponent={<Skeleton />}>
                  <div style={{ display: 'flex', justifyContent: 'center' }}>
                    {featuredReviews.length === 0 ? (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={<span>No featured review</span>}
                      />
                    ) : (
                      <div
                        style={{
                          backgroundColor: BACKGROUND_COLOR,
                          boxShadow: '0 8px 16px 0 rgba(0,0,0,0.2), 0 6px 20px 0 rgba(0,0,0,0.19)',
                          width: isMobile || device === 'mobile' ? '280px' : '350px',
                          borderRadius: '3rem',
                          padding:
                            isMobile || device === 'mobile'
                              ? '5px 10px 5px 10px'
                              : '10px 20px 10px 10px',
                          margin: '20px 0px 20px 0px',
                        }}>
                        <Carousel
                          autoplay
                          dots={false}
                          effect="fade"
                          style={{ backgroundColor: BACKGROUND_COLOR, borderRadius: '2rem' }}>
                          {featuredReviews.map(item => (
                            <ReviewWidget
                              key={item.reviewId}
                              review={item}
                              isUIMobile={device === 'mobile'}
                            />
                          ))}
                        </Carousel>
                      </div>
                    )}
                  </div>
                </LoadableContainer>
              </Col>

              {!isMobile && (
                <Col sm={24} xl={1} style={{ textAlign: 'center' }}>
                  <Divider type="vertical" style={{ height: '100%' }} />
                </Col>
              )}

              <Col sm={24} xl={12}>
                <h3>Install</h3>
                <p>Copy and add this script to your web page. You only need to copy it once.</p>
                {isLoadingWidget ? (
                  <Skeleton />
                ) : (
                  <>
                    {isLoaded && (
                      <TextArea
                        style={{ marginTop: '10px' }}
                        contentEditable={false}
                        value={`<div class="review-circle-widget-${user.selected_location.location_id}" />\n<script src="https://app.reviewcircle.com/widget/index.js" defer></script>`}
                        autoSize={{ minRows: 2 }}
                      />
                    )}

                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'baseline',
                        marginBottom: '10px',
                      }}>
                      <Tooltip title={isCopied ? 'Copied' : 'Copy'}>
                        <Button
                          type="link"
                          size="small"
                          icon={isCopied ? <CheckOutlined /> : <CopyOutlined />}
                          onClick={handleCopy}>
                          {isCopied ? 'Copied' : 'Copy'}
                        </Button>
                      </Tooltip>

                      {/* <i>
                      <a href="https://reviewcircle.com">
                        Find out how to embed the widget to your website
                      </a>
                    </i> */}
                    </div>
                  </>
                )}
              </Col>
            </Row>
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={<span>No location selected</span>}
            />
          )}
        </LoadableContainer>
      </Card>
    </div>
  );
};

export default WidgetScreen;
